import Button from '@atoms/Button/Button';
import { IArticle } from '@models/IArticle';
import { IProduct } from '@models/IProduct';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import { ISearchResult } from '@models/ISearchResult';
import { ISearchSummaryRowResult } from '@models/ISearchSummaryRowResult';
import ArticleCard from '@molecules/ArticleCard/ArticleCard';
import { Fragment } from 'react';
import SearchSummaryProductsRow from './SearchSummaryProductsRow';

interface Props extends ISearchSummaryRowResult {
  readonly hidePrices: boolean;
  readonly value: string;
  readonly productsStatusLabels: IProductListingStatusLabels;
}

const SearchSummaryRow = ({
  hidePrices,
  pageType,
  productsStatusLabels,
  results,
  title,
  totalCount,
  value,
  viewAllLink,
}: Props) => {
  if (results.length === 0) return null;

  const displayedPages =
    pageType === 'page'
      ? ((results as ISearchResult[])?.filter(
          (result) => result.title && result.url
        ) ?? [])
      : [];

  return (
    <div>
      <div className="container mb-7.5 flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
        <p
          className="h4"
          data-testid="CT-48-Search-Summary-Results-Row-Title"
        >{`${title} (${totalCount})`}</p>

        {viewAllLink?.url && viewAllLink?.name && (
          <div>
            <Button
              link={{
                ...viewAllLink,
                url: `${viewAllLink.url}?PageType=${pageType}&query=${value}`,
              }}
              dataTestId="CT-48-Search-Summary-Results-Row-View-All-Link"
            />
          </div>
        )}
      </div>

      {pageType === 'product' && (
        <SearchSummaryProductsRow
          hidePrices={hidePrices}
          products={results as IProduct[]}
          productsStatusLabels={productsStatusLabels}
        />
      )}

      {pageType === 'article' && (
        <div
          className="container flex w-full flex-col gap-7.5 lg:grid lg:grid-cols-3 lg:gap-4"
          data-testid="CT-48-Search-Summary-Results-Row-Articles"
        >
          {(results as IArticle[]).map((result, index) => (
            <ArticleCard
              key={`article-${result.title}`}
              {...result}
              isLightMode
              dataTestId={`CT-48-Search-Summary-Results-Row-Article-Card-${index}`}
            />
          ))}
        </div>
      )}

      {pageType === 'page' && (
        <div
          className="container grid grid-cols-[auto_1fr] items-center gap-x-[34px] pt-2 lg:gap-x-13 lg:pt-10"
          data-testid="CT-48-Search-Summary-Results-Row-Pages"
        >
          {displayedPages.map(
            ({ pageTypeLabel, title: resultTitle, url }, index) => (
              <Fragment key={`search-result-${resultTitle}`}>
                <div
                  className="flex items-center py-2 lg:py-6"
                  data-testid={`CT-48-Search-Summary-Results-Row-Page-Result-${index}`}
                >
                  <a
                    href={url}
                    target="_self"
                    className="flex"
                    data-testid={`CT-48-Search-Summary-Results-Row-Page-Result-PageType-Link-${index}`}
                  >
                    <span className="body-two inline-block max-w-full truncate whitespace-nowrap rounded-[100px] bg-black-100 pt-2 pr-4 pb-2 pl-4 text-white decoration-1 hover:underline md:w-auto md:max-w-auto">
                      {pageTypeLabel}
                    </span>
                  </a>
                </div>

                <div className="flex items-center py-2 lg:py-6">
                  <a
                    href={url}
                    target="_self"
                    className="h6 w-auto text-black-100 decoration-1 hover:underline"
                    data-testid={`CT-48-Search-Summary-Results-Row-Page-Result-Title-${index}`}
                  >
                    {resultTitle}
                  </a>
                </div>

                {index < displayedPages.length - 1 && (
                  <div className="col-span-full h-[1px] bg-grey-line" />
                )}
              </Fragment>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default SearchSummaryRow;
